import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Plus } from "lucide-react";
import { api } from "../../api/client";
import { btnPrimary, type as text, theme } from "../../theme";
import { SheetCard, StatusTag, type StatusTone } from "../../components/Drawing";
import { LoadingBlock } from "../../components/Spinner";
import { NextSteps } from "../../components/NextSteps";
import { EntityTagsRow } from "../../components/EntityTags";
import { useDeepLinkScroll } from "../../hooks/useDeepLinkScroll";
import { EmptyState, Field, ModalHeader, ModalOverlay, input, textarea, secondaryBtn, dangerBtn, toDateInput, dateInputToIso, fmtDateOnly, todayInput, tableHeadRow } from "../resilience/shared";
import { AttentionList, FrameworkPick, Meta, OwnerField, REVIEW_INTERVALS, attentionFor, msRef, personName, standardsOf, useMembers, useMsEnrolment, useMsPermissions, words, type Person } from "./shared";
import { attempt, useClearErrorOnDialog, useMsOverview, useRegister } from "./useRegister";

interface CompetenceRecord {
  id: string; userId: string | null; user: Person | null; personName: string | null; status: string; standing: string;
  evidence: string | null; evidenceDocumentId: string | null; evidenceDocument: { id: string; fileName: string } | null;
  assessedAt: string | null; expiresAt: string | null; action: string | null; actionDueAt: string | null;
}
interface Requirement {
  id: string; displayId: number | null; role: string; frameworkIds: string[]; competence: string; basis: string; evidenceRequired: string | null;
  ownerUserId: string | null; owner: Person | null; ownerName: string | null; reviewIntervalMonths: number; nextReviewAt: string | null;
  records: CompetenceRecord[];
  coverage: { people: number; competent: number; developing: number; gaps: number; expired: number; actionsOverdue: number };
}

const STANDING: Record<string, { tone: StatusTone; label: string }> = {
  competent: { tone: "met", label: "Competent" },
  expiring: { tone: "partial", label: "Evidence expiring" },
  expired: { tone: "gap", label: "Evidence lapsed" },
  developing: { tone: "partial", label: "Developing" },
  gap: { tone: "gap", label: "Gap" },
};

type ReqForm = { role: string; frameworkIds: string[]; competence: string; basis: string; evidenceRequired: string; ownerUserId: string; ownerName: string; reviewIntervalMonths: number; nextReviewAt: string };
type RecForm = { requirement: Requirement; id: string | null; userId: string; personName: string; status: string; evidence: string; assessedAt: string; expiresAt: string; action: string; actionDueAt: string };

export function CompetenceRegister() {
  const { data, loading, error, reload } = useRegister<{ requirements: Requirement[] }>("/management-system/competence");
  const overview = useMsOverview();
  const enrolled = useMsEnrolment();
  const members = useMembers();
  const { canWrite } = useMsPermissions();
  const [params] = useSearchParams();
  const [editing, setEditing] = useState<{ id: string | null; form: ReqForm } | null>(null);
  const [record, setRecord] = useState<RecForm | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  useDeepLinkScroll(params.get("requirement"), !!data && !overview.loading);
  useClearErrorOnDialog(setFormError, !!editing, !!record);

  if (loading) return <LoadingBlock />;
  if (error || !data) return <p style={{ ...text.body, color: theme.color.dangerText }}>{error}</p>;
  const refresh = () => Promise.all([reload(), overview.reload()]);

  const blankReq: ReqForm = { role: "", frameworkIds: [], competence: "", basis: "training", evidenceRequired: "", ownerUserId: "", ownerName: "", reviewIntervalMonths: 12, nextReviewAt: "" };

  const saveReq = async () => {
    if (!editing) return;
    const f = editing.form;
    const body = { ...f, evidenceRequired: f.evidenceRequired || null, ownerUserId: f.ownerUserId || null, ownerName: f.ownerName || null, nextReviewAt: dateInputToIso(f.nextReviewAt) };
    if (await attempt(() => (editing.id ? api.put(`/management-system/competence/${editing.id}`, body) : api.post("/management-system/competence", body)), setFormError, "Could not save the requirement")) {
      setEditing(null);
      await refresh();
    }
  };

  const saveRecord = async () => {
    if (!record) return;
    const body = {
      userId: record.userId || null, personName: record.userId ? null : record.personName || null, status: record.status, evidence: record.evidence || null,
      assessedAt: dateInputToIso(record.assessedAt), expiresAt: dateInputToIso(record.expiresAt), action: record.action || null, actionDueAt: dateInputToIso(record.actionDueAt),
    };
    const path = `/management-system/competence/${record.requirement.id}/records${record.id ? `/${record.id}` : ""}`;
    if (await attempt(() => (record.id ? api.put(path, body) : api.post(path, body)), setFormError, "Could not save the record")) {
      setRecord(null);
      await refresh();
    }
  };

  const reviewed = async (r: Requirement) => {
    if (await attempt(() => api.post(`/management-system/competence/${r.id}/reviewed`), setFormError, "Could not record the review")) await refresh();
  };
  const remove = async (r: Requirement) => {
    if (!window.confirm(`Delete ${msRef("competence", r.displayId)} ${r.role} and its records?`)) return;
    if (await attempt(() => api.del(`/management-system/competence/${r.id}`), setFormError, "Could not delete the requirement")) await refresh();
  };
  const removeRecord = async (r: Requirement, x: CompetenceRecord) => {
    if (!window.confirm("Delete this record?")) return;
    if (await attempt(() => api.del(`/management-system/competence/${r.id}/records/${x.id}`), setFormError, "Could not delete the record")) await refresh();
  };

  const f = editing?.form;
  const set = (patch: Partial<ReqForm>) => editing && setEditing({ ...editing, form: { ...editing.form, ...patch } });

  return (
    <>
      <AttentionList items={attentionFor(overview.data?.attention ?? [], "ms_competence")} />
      {formError && !editing && !record && <p style={{ ...text.body, color: theme.color.dangerText }}>{formError}</p>}
      <SheetCard label="Competence by role" reference="7.2" actions={canWrite ? <button type="button" style={secondaryBtn} onClick={() => setEditing({ id: null, form: blankReq })}><Plus size={12} /> Role</button> : undefined}>
        {data.requirements.length === 0 ? (
          <EmptyState
            title="No competence requirements recorded"
            detail="For each role whose work affects the management system, record the competence it needs, then the people in it and the evidence that they hold it — a certificate, a course, an appraisal, experience — with any action taken to close a gap."
            action={canWrite ? <button type="button" style={btnPrimary} onClick={() => setEditing({ id: null, form: blankReq })}>Add a role</button> : undefined}
          />
        ) : (
          <div style={{ display: "grid", gap: 10 }}>
            {data.requirements.map((r) => (
              <div key={r.id} data-deeplink={r.id} style={{ border: "1px solid var(--c-border)", borderRadius: 3, padding: "10px 12px", background: "var(--c-panel)" }}>
                <div style={{ display: "flex", gap: 10, alignItems: "baseline", flexWrap: "wrap" }}>
                  <span style={{ ...text.ref, color: theme.color.ref }}>{msRef("competence", r.displayId)}</span>
                  <span style={text.itemTitle}>{r.role}</span>
                  <span style={text.meta}>{r.coverage.competent} of {r.coverage.people} competent</span>
                  {r.coverage.gaps + r.coverage.expired > 0 && <StatusTag tone="gap">{r.coverage.gaps + r.coverage.expired} gap{r.coverage.gaps + r.coverage.expired === 1 ? "" : "s"}</StatusTag>}
                  {canWrite && (
                    <span style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
                      <button type="button" style={secondaryBtn} onClick={() => setRecord({ requirement: r, id: null, userId: "", personName: "", status: "competent", evidence: "", assessedAt: todayInput(), expiresAt: "", action: "", actionDueAt: "" })}>Add person</button>
                      <button type="button" style={secondaryBtn} onClick={() => void reviewed(r)}>Mark reviewed</button>
                      <button type="button" style={secondaryBtn} onClick={() => setEditing({ id: r.id, form: { role: r.role, frameworkIds: r.frameworkIds, competence: r.competence, basis: r.basis, evidenceRequired: r.evidenceRequired ?? "", ownerUserId: r.ownerUserId ?? "", ownerName: r.ownerName ?? "", reviewIntervalMonths: r.reviewIntervalMonths, nextReviewAt: toDateInput(r.nextReviewAt) } })}>Edit</button>
                      <button type="button" style={dangerBtn} onClick={() => void remove(r)}>Delete</button>
                    </span>
                  )}
                </div>
                <p style={{ ...text.body, margin: "4px 0", whiteSpace: "pre-line" }}>{r.competence}</p>
                <Meta>
                  <span>Basis: {words(r.basis)}</span>
                  {r.evidenceRequired && <span>Evidence expected: {r.evidenceRequired}</span>}
                  <span>Owner: {personName(r.owner, r.ownerName)}</span>
                  <span>Next review: {fmtDateOnly(r.nextReviewAt)}</span>
                  <span>Standards: {standardsOf(r.frameworkIds, enrolled)}</span>
                </Meta>
                {r.records.length > 0 && (
                  <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 8 }}>
                    <thead><tr style={{ ...tableHeadRow, borderBottom: "1px solid var(--c-line)" }}><th style={{ padding: 4 }}>Person</th><th style={{ padding: 4 }}>Standing</th><th style={{ padding: 4 }}>Evidence</th><th style={{ padding: 4 }}>Expires</th><th style={{ padding: 4 }}>Action to close a gap</th><th /></tr></thead>
                    <tbody>
                      {r.records.map((x) => {
                        const st = STANDING[x.standing] ?? STANDING.gap;
                        return (
                          <tr key={x.id} style={{ ...text.body, borderBottom: "1px solid var(--c-border)", verticalAlign: "top" }}>
                            <td style={{ padding: 4 }}>{x.user ? personName(x.user, null) : x.personName}</td>
                            <td style={{ padding: 4 }}><StatusTag tone={st.tone}>{st.label}</StatusTag></td>
                            <td style={{ padding: 4 }}>{x.evidence ?? "—"}{x.evidenceDocument && <div><a href={`/documents?document=${x.evidenceDocument.id}`} style={{ color: theme.color.ref }}>{x.evidenceDocument.fileName}</a></div>}</td>
                            <td style={{ padding: 4 }}>{fmtDateOnly(x.expiresAt)}</td>
                            <td style={{ padding: 4 }}>{x.action ?? "—"}{x.actionDueAt && <div style={text.hint}>by {fmtDateOnly(x.actionDueAt)}</div>}</td>
                            <td style={{ padding: 4, whiteSpace: "nowrap" }}>
                              {canWrite && <button type="button" style={secondaryBtn} onClick={() => setRecord({ requirement: r, id: x.id, userId: x.userId ?? "", personName: x.personName ?? "", status: x.status, evidence: x.evidence ?? "", assessedAt: toDateInput(x.assessedAt), expiresAt: toDateInput(x.expiresAt), action: x.action ?? "", actionDueAt: toDateInput(x.actionDueAt) })}>Edit</button>}
                              {canWrite && <button type="button" style={{ ...dangerBtn, marginLeft: 4 }} onClick={() => void removeRecord(r, x)}>Delete</button>}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
                <EntityTagsRow entity={{ kind: "ms_competence", id: r.id }} readOnly={!canWrite} style={{ marginTop: 6 }} />
              </div>
            ))}
          </div>
        )}
      </SheetCard>

      {editing && f && (
        <ModalOverlay onClose={() => setEditing(null)}>
          <ModalHeader title={editing.id ? "Edit competence requirement" : "Add a role"} onClose={() => setEditing(null)} />
          <div style={{ padding: 20, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            {/* Ticks as the form is filled in: what 7.2 expects of each role's competence. */}
            <div style={{ gridColumn: "1 / -1" }}>
              <NextSteps items={[
                { done: !!f.role.trim(), label: "Name the role" },
                { done: !!f.competence.trim(), label: "Say what competence the role needs", hint: "7.2 a: determine the competence of people whose work affects performance." },
                { done: !!f.evidenceRequired.trim(), label: "Say what evidence of competence is expected", hint: "7.2 d: retain documented information as evidence." },
                { done: !!(f.ownerUserId || f.ownerName.trim()), label: "Name an owner" },
              ]} />
            </div>
            <Field label="Role" span2><input style={input} maxLength={200} value={f.role} onChange={(e) => set({ role: e.target.value })} /></Field>
            <Field label="Competence the role needs" span2><textarea style={textarea} rows={3} value={f.competence} onChange={(e) => set({ competence: e.target.value })} /></Field>
            <Field label="Acquired through">
              <select style={input} value={f.basis} onChange={(e) => set({ basis: e.target.value })}>
                {["education", "training", "experience", "qualification"].map((b) => <option key={b} value={b}>{words(b)}</option>)}
              </select>
            </Field>
            <Field label="Evidence expected"><input style={input} value={f.evidenceRequired} onChange={(e) => set({ evidenceRequired: e.target.value })} /></Field>
            <OwnerField userId={f.ownerUserId} name={f.ownerName} members={members} onChange={(o) => set({ ownerUserId: o.userId, ownerName: o.name })} />
            <Field label="Review every">
              <select style={input} value={f.reviewIntervalMonths} onChange={(e) => set({ reviewIntervalMonths: Number(e.target.value) })}>
                {REVIEW_INTERVALS.map((m) => <option key={m} value={m}>{m} months</option>)}
              </select>
            </Field>
            <Field label="Next review"><input type="date" style={input} value={f.nextReviewAt} onChange={(e) => set({ nextReviewAt: e.target.value })} /></Field>
            <FrameworkPick allowed={enrolled} value={f.frameworkIds} onChange={(ids) => set({ frameworkIds: ids })} />
            {formError && <p style={{ gridColumn: "1 / -1", margin: 0, ...text.body, color: theme.color.dangerText }}>{formError}</p>}
            <div style={{ gridColumn: "1 / -1", display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <button type="button" style={secondaryBtn} onClick={() => setEditing(null)}>Cancel</button>
              <button type="button" style={btnPrimary} disabled={!f.role.trim() || !f.competence.trim()} onClick={() => void saveReq()}>Save</button>
            </div>
          </div>
        </ModalOverlay>
      )}

      {record && (
        <ModalOverlay onClose={() => setRecord(null)}>
          <ModalHeader title={`${record.id ? "Edit" : "Add"} person: ${record.requirement.role}`} onClose={() => setRecord(null)} />
          <div style={{ padding: 20, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            {/* Ticks as the form is filled in: 7.2 c / d — evidence that the person is competent, or the action to get there. */}
            <div style={{ gridColumn: "1 / -1" }}>
              <NextSteps items={[
                { done: !!(record.userId || record.personName.trim()), label: "Name the person" },
                { done: !!record.evidence.trim(), label: "Record the evidence relied on", hint: "7.2 d: a certificate, course, appraisal or experience." },
                { done: !!record.assessedAt, label: "Give the date it was assessed" },                ...(record.status === "developing" || record.status === "gap" ? [
                  { done: !!record.action.trim(), label: "Say what action will close the gap", hint: "7.2 c: take action to acquire the competence." },
                  { done: !!record.actionDueAt, label: "Set a due date for the action" },
                ] : []),
              ]} />
            </div>
            <OwnerField label="Person" userId={record.userId} name={record.personName} members={members} onChange={(o) => setRecord({ ...record, userId: o.userId, personName: o.name })} />
            <Field label="Standing">
              <select style={input} value={record.status} onChange={(e) => setRecord({ ...record, status: e.target.value })}>
                <option value="competent">Competent</option>
                <option value="developing">Developing</option>
                <option value="gap">Gap</option>
              </select>
            </Field>
            <Field label="Evidence" span2 help="The certificate, course, appraisal or experience relied on."><textarea style={textarea} rows={2} value={record.evidence} onChange={(e) => setRecord({ ...record, evidence: e.target.value })} /></Field>
            <Field label="Assessed on"><input type="date" style={input} value={record.assessedAt} onChange={(e) => setRecord({ ...record, assessedAt: e.target.value })} /></Field>
            <Field label="Evidence expires"><input type="date" style={input} value={record.expiresAt} onChange={(e) => setRecord({ ...record, expiresAt: e.target.value })} /></Field>
            <Field label="Action to acquire the competence" span2><textarea style={textarea} rows={2} value={record.action} onChange={(e) => setRecord({ ...record, action: e.target.value })} /></Field>
            <Field label="Action due by"><input type="date" style={input} value={record.actionDueAt} onChange={(e) => setRecord({ ...record, actionDueAt: e.target.value })} /></Field>
            {formError && <p style={{ gridColumn: "1 / -1", margin: 0, ...text.body, color: theme.color.dangerText }}>{formError}</p>}
            <div style={{ gridColumn: "1 / -1", display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <button type="button" style={secondaryBtn} onClick={() => setRecord(null)}>Cancel</button>
              <button type="button" style={btnPrimary} disabled={!record.userId && !record.personName.trim()} onClick={() => void saveRecord()}>Save</button>
            </div>
          </div>
        </ModalOverlay>
      )}
    </>
  );
}
